import type { SupportedLanguage } from "@pimo/pimo-l10n";

import { BACKEND_URL } from "../env";
import { ApiError } from "../types/api-error";
import { App } from "../types/entities";

export type FrequencyUnit = "weekly" | "monthly" | "yearly";

export interface ReportingPeriod {
  startDate: string;
  endDate: string;
}

export interface FrequencySettings {
  unit: FrequencyUnit;
  interval: number;
  weeklyDay?: string;
  monthlyDay?: number;
  yearlyDate?: string;
}

export interface CreateAppData {
  name: string;
  description?: string;
  useCase?: string;
  reportingPeriod?: ReportingPeriod;
  frequency?: FrequencySettings;
  url?: string;
  clientId?: string;
  /**
   * Active Home Portal UI language at creation time. Forwarded to
   * test-backend, where it is persisted as `configuration.defaultLanguage`
   * and used to seed localized sample data (falls back to English if
   * omitted).
   */
  language?: SupportedLanguage;
}

export type DeleteQueueEntryStatus =
  "pending" | "running" | "success" | "failed";

export interface DeleteQueueEntry {
  appId: string;
  appName: string;
  clientId: string;
  attempts: number;
  status: DeleteQueueEntryStatus;
  lastError?: string;
  userKey: string;
}

export interface DeleteQueueStatus {
  queueId?: string | null;
  failureCount?: number;
  userKey: string;
  isRunning: boolean;
  entries: DeleteQueueEntry[];
  selectedEntry: DeleteQueueEntry | null;
}

export interface EnqueueAppDeletionResult {
  queueId?: string;
  failureCount?: number;
  success: boolean;
  userKey?: string;
  appId?: string;
  appName?: string;
  clientId?: string;
  entry?: DeleteQueueEntry;
  isRunning?: boolean;
  queueLength?: number;
  queuePosition?: number;
  error?: ApiError;
}

export async function fetchApps(clientId?: string): Promise<App[]> {
  try {
    const url = clientId
      ? `${BACKEND_URL}/apps?clientId=${encodeURIComponent(clientId)}`
      : `${BACKEND_URL}/apps`;
    const response = await fetch(url, {
      credentials: "include",
    });

    if (response.status >= 400) {
      throw new Error(`Fetch apps failed with status ${response.status}.`);
    }

    const apps = (await response.json()) as App[];
    return apps;
  } catch {
    return [];
  }
}

export async function checkAppNameAvailable(
  clientId: string,
  name: string
): Promise<{ available: boolean }> {
  const trimmed = name?.trim() ?? "";
  if (!trimmed || !clientId) return { available: false };
  try {
    const url = `${BACKEND_URL}/apps/check-name?${new URLSearchParams({
      name: trimmed,
      clientId,
    })}`;
    const response = await fetch(url, { credentials: "include" });
    if (response.status >= 400) return { available: false };
    const data = (await response.json()) as { available: boolean };
    return { available: data?.available ?? false };
  } catch {
    return { available: false };
  }
}

export interface CreateAppResult {
  success: boolean;
  app?: App;
  error?: ApiError;
}

async function parseErrorResponse(response: Response): Promise<ApiError> {
  try {
    const errorData = await response.json().catch(() => ({}));
    const code =
      errorData?.code ??
      (response.status === 409
        ? "conflict"
        : response.status === 404
          ? "not_found"
          : undefined);
    const message =
      errorData?.message || "Failed to create app. Please try again.";
    return {
      status: response.status,
      code,
      message,
      referenceId: errorData?.referenceId,
    };
  } catch {
    console.warn("Failed to parse error response from server");
    return {
      status: response.status,
      message: "Failed to create app. Please try again.",
    };
  }
}

export async function deleteApp(
  appId: string
): Promise<{ success: boolean; error?: ApiError }> {
  try {
    const response = await fetch(`${BACKEND_URL}/apps/${appId}`, {
      method: "DELETE",
      credentials: "include",
    });

    if (response.status >= 400) {
      let parsed = {
        status: response.status,
        message: "Failed to delete app. Please try again.",
      } as ApiError;

      try {
        const errorData = await response.json().catch(() => ({}));
        parsed = {
          status: response.status,
          code: errorData?.code,
          message: errorData?.message || parsed.message,
        };
      } catch {
        console.warn("Failed to parse error response from server");
      }

      return {
        success: false,
        error: parsed,
      };
    }

    return { success: true };
  } catch {
    return {
      success: false,
      error: { message: "An unexpected error occurred. Please try again." },
    };
  }
}

export async function enqueueAppDeletion(
  appId: string
): Promise<EnqueueAppDeletionResult> {
  try {
    const response = await fetch(`${BACKEND_URL}/apps/${appId}/delete-queue`, {
      method: "POST",
      credentials: "include",
      cache: "no-store",
    });

    if (response.status >= 400) {
      let parsed = {
        status: response.status,
        message: "Failed to queue app deletion. Please try again.",
      } as ApiError;

      try {
        const errorData = await response.json().catch(() => ({}));
        parsed = {
          status: response.status,
          code: errorData?.code,
          message: errorData?.message || parsed.message,
        };
      } catch {
        console.warn("Failed to parse error response from server");
      }

      return {
        success: false,
        error: parsed,
      };
    }

    const result = (await response.json()) as Omit<
      EnqueueAppDeletionResult,
      "success" | "error"
    >;

    return {
      success: true,
      ...result,
    };
  } catch {
    return {
      success: false,
      error: {
        message: "An unexpected error occurred. Please try again.",
      },
    };
  }
}

export async function fetchAppDeleteQueueStatus(
  clientId?: string
): Promise<DeleteQueueStatus | null> {
  try {
    const url = clientId
      ? `${BACKEND_URL}/apps/delete-queue/status?${new URLSearchParams({
          clientId,
        })}`
      : `${BACKEND_URL}/apps/delete-queue/status`;

    const response = await fetch(url, {
      credentials: "include",
      cache: "no-store",
    });

    if (response.status >= 400) {
      return null;
    }

    return (await response.json()) as DeleteQueueStatus;
  } catch {
    return null;
  }
}

export async function createApp(
  appData: CreateAppData
): Promise<CreateAppResult> {
  try {
    const response = await fetch(`${BACKEND_URL}/apps`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(appData),
    });

    if (response.status >= 400) {
      const parsedError = await parseErrorResponse(response);
      return {
        success: false,
        error: parsedError,
      };
    }

    const app = (await response.json()) as App;
    return {
      success: true,
      app,
    };
  } catch {
    return {
      success: false,
      error: {
        message: "An unexpected error occurred. Please try again.",
      },
    };
  }
}
